import { Button, notification, Result, Spin } from 'antd'
import { observer } from 'mobx-react-lite'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import Images from 'src/common/Images'
import { GLOBAL_CLIENT } from 'src/helpers/GlobalClient'
import { SOCKET_EVENT, getBankTransferContent, statusFund } from 'src/helpers/constants'
import { formatCurrency } from 'src/helpers/functions'
import useModal from 'src/helpers/modal/useModal'
import { socket } from 'src/helpers/socket/socket'
import { useStore } from 'src/hooks'

export const PENDING_PAYMENT_KEY = 'PENDING_PAYMENT'

type BankAccount = {
   BANK_ID: string
   ACCOUNT_NO: string
   ACCOUNT_NAME: string
   DESCRIPTION: string
}

type PendingPayment = {
   amount: number
   code: string
   content: string
   bankAccount: BankAccount
}

const getPendingPayment = (): PendingPayment | null => {
   try {
      const raw = sessionStorage.getItem(PENDING_PAYMENT_KEY)
      return raw ? JSON.parse(raw) : null
   } catch {
      return null
   }
}

const PaymentScreen: React.FC = () => {
   const { t } = useTranslation()
   const history = useHistory()
   const { confirm } = useModal()
   const AuthStore = useStore('AuthStore')
   const FundStore = useStore('FundStore')

   const [payment] = useState<PendingPayment | null>(() => getPendingPayment())
   const [success, setSuccess] = useState(false)
   const redirectTimer = useRef<any>(null)
   const doneRef = useRef(false)

   // Xử lý khi xác nhận nạp tiền thành công (dùng chung cho socket và polling)
   const markSuccess = useCallback(() => {
      if (doneRef.current) return
      doneRef.current = true
      setSuccess(true)
      sessionStorage.removeItem(PENDING_PAYMENT_KEY)
      notification.success({
         message: t(GLOBAL_CLIENT.thongbao),
         description: t(GLOBAL_CLIENT.ckthanhcong)
      })
      redirectTimer.current = setTimeout(() => history.replace('/'), 3000)
   }, [history, t])

   // Không có thông tin thanh toán → quay về trang chủ
   useEffect(() => {
      if (!payment) {
         history.replace('/')
      }
   }, [payment, history])

   // Lắng nghe socket báo nạp tiền thành công để tự đóng màn hình
   useEffect(() => {
      if (!payment) return

      if (!socket.connected) {
         socket.connect()
      }

      const onRechargeSuccess = (data: any) => {
         const matchByCode = data?.code && payment.code && String(data.code) === String(payment.code)
         const matchByAccount =
            data?.accountId && AuthStore?.data_getUserInfo?.id
               ? String(data.accountId) === String(AuthStore.data_getUserInfo.id)
               : false

         if (matchByCode || matchByAccount) {
            markSuccess()
         }
      }

      socket.on(SOCKET_EVENT.RECHARGE_SUCCESS, onRechargeSuccess)

      return () => {
         socket.off(SOCKET_EVENT.RECHARGE_SUCCESS, onRechargeSuccess)
         if (redirectTimer.current) clearTimeout(redirectTimer.current)
      }
   }, [payment, AuthStore, markSuccess])

   // Fallback: 10s gọi API /funds lọc theo code 1 lần để kiểm tra fund đã được phê duyệt chưa
   // (phòng trường hợp socket chưa gửi về được)
   useEffect(() => {
      if (!payment) return

      const interval = setInterval(async () => {
         if (doneRef.current) return
         const fund = await FundStore.checkFundByCode(payment.code)
         if (fund && Number(fund.status) === statusFund.APPROVE) {
            markSuccess()
         }
      }, 5000)

      return () => clearInterval(interval)
   }, [payment, FundStore, markSuccess])

   if (!payment) return null

   const { amount, bankAccount } = payment

   // Nội dung chuyển khoản dạng POD + mã (vd: PODABC123). Fallback cho dữ liệu cũ.
   const transferContent = payment.content || getBankTransferContent(payment.code || bankAccount.DESCRIPTION)

   // QR SePay: https://qr.sepay.vn/img?bank=...&acc=...&template=compact&amount=...&des=POD<code>
   const qrUrl = `https://qr.sepay.vn/img?bank=${bankAccount.BANK_ID}&acc=${bankAccount.ACCOUNT_NO}&template=compact&amount=${amount}&des=${transferContent}`

   const copyText = (value: string, label: string) => {
      if (typeof navigator.clipboard !== 'undefined' && window.isSecureContext) {
         navigator.clipboard.writeText(value).then(
            () => {
               notification.open({
                  message: t(GLOBAL_CLIENT.thongbao),
                  description: `${label}: ${value}`
               })
            },
            () => null
         )
      }
   }

   const handleCancel = () => {
      // @ts-ignore
      confirm({
         title: 'Xác nhận',
         icon: null,
         content: 'Bạn thực sự muốn hủy lệnh chuyển tiền?',
         okText: 'Có',
         cancelText: 'Không',
         onOk() {
            sessionStorage.removeItem(PENDING_PAYMENT_KEY)
            history.replace('/')
         }
      })
   }

   if (success) {
      return (
         <div style={wrapperStyle}>
            <Result
               status="success"
               title={t(GLOBAL_CLIENT.ckthanhcong)}
               subTitle="Hệ thống đã nhận được thanh toán của bạn. Đang chuyển về trang chủ..."
               extra={[
                  <Button type="primary" key="home" onClick={() => history.replace('/')}>
                     Về trang chủ
                  </Button>
               ]}
            />
         </div>
      )
   }

   const InfoRow = ({ label, value, copyable }: { label: string; value: string; copyable?: boolean }) => (
      <div style={rowStyle}>
         <span style={{ color: '#888' }}>{label}</span>
         <span
            style={{ fontWeight: 600, cursor: copyable ? 'pointer' : 'default', display: 'flex', alignItems: 'center' }}
            onClick={copyable ? () => copyText(value, label) : undefined}
         >
            {value}
            {copyable && (
               <img src={Images.ic_copy} alt="" style={{ width: 18, height: 18, marginLeft: 8 }} />
            )}
         </span>
      </div>
   )

   return (
      <div style={wrapperStyle}>
         <div style={cardStyle}>
            <h2 style={{ textAlign: 'center', marginBottom: 4 }}>{t(GLOBAL_CLIENT.deposit)}</h2>
            <p style={{ textAlign: 'center', color: '#888', marginBottom: 16 }}>
               Quét mã QR hoặc chuyển khoản theo thông tin bên dưới. Màn hình sẽ tự đóng khi nhận được thanh toán.
            </p>

            <div style={{ textAlign: 'center', marginBottom: 16 }}>
               <img src={qrUrl} alt="QR" style={{ width: 240, maxWidth: '100%' }} />
            </div>

            <InfoRow label="Ngân hàng" value={bankAccount.BANK_ID} />
            <InfoRow label="Số tài khoản" value={bankAccount.ACCOUNT_NO} copyable />
            {bankAccount.ACCOUNT_NAME ? <InfoRow label="Chủ tài khoản" value={bankAccount.ACCOUNT_NAME} /> : null}
            <InfoRow label="Nội dung" value={transferContent} copyable />
            <InfoRow label="Số tiền" value={`${formatCurrency(amount, true)} đ`} copyable />

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 20 }}>
               <Spin size="small" />
               <span style={{ color: '#888' }}>Đang chờ thanh toán...</span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
               <Button type="dashed" onClick={handleCancel}>
                  {t(GLOBAL_CLIENT.huy)}
               </Button>
            </div>
         </div>
      </div>
   )
}

const wrapperStyle: React.CSSProperties = {
   display: 'flex',
   justifyContent: 'center',
   alignItems: 'flex-start',
   padding: 24,
   width: '100%'
}

const cardStyle: React.CSSProperties = {
   width: 420,
   maxWidth: '100%',
   background: '#fff',
   borderRadius: 12,
   padding: 24,
   boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
}

const rowStyle: React.CSSProperties = {
   display: 'flex',
   justifyContent: 'space-between',
   alignItems: 'center',
   padding: '8px 0',
   borderBottom: '1px solid #f0f0f0'
}

export default observer(PaymentScreen)
